import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { NavigationService } from '../navigation/navigation.service';
import { AuthService } from '../service/auth.service';
import { HttpClientService } from '../service/http-client.service';
import { isSuccess, Response } from '../service/response';
import { isNullOrUndefined, isStringEmpty, UtilsService } from '../service/utils.service';
import { UserInfo } from '../user/user-management/models/user.model';
import { UserService } from '../user/user-management/services/user.service';

@Injectable({
  providedIn: 'root'
})
export class SignInService {
  private dialogTitle = '로그인';
  private userInfo?: UserInfo;

  constructor(
    private router: Router,
    private httpClient: HttpClientService,
    private userService: UserService,
    private auth: AuthService,
    private navigation: NavigationService,
    private utils: UtilsService
  ) {
    this.userInfo = this.getUserInfoFromStorage();
    this.setUserInfoToMenu(this.userInfo);
  }

  getCaptcha() {
    const url = '/createCaptchaImage';
    return this.httpClient.get(url);
  }

  async requestSignIn(singInData: any) {
    const url = '/signIn';
    const body = singInData;
    const res = await this.post(url, body).toPromise() as Response;
    if (isSuccess(res.resultCode)) {
      this.userInfo = res.data.userInfo;
      if (this.userInfo) {
        const auth = res.data.auth;
        this.httpClient.saveAuth(auth);

        this.saveUserInfo(this.userInfo);
        this.setUserInfoToMenu(this.userInfo);
      }
    }
    return res;
  }

  // 로그인 2차 인증 — OTP 발송 요청.
  //   mfa_required_yn='N' 사용자는 1차 응답에 auth 토큰까지 포함 → 즉시 저장.
  //   change_password 도 토큰까지 발급된 상태 (서버 API 정의서 4.1 절) — 동일하게 저장.
  //   showLoading=false: 컴포넌트의 race-safe 로딩으로 제어 (HttpClientService 의 setTimeout fallback 회피).
  async requestOtpForSignIn(signInData: any) {
    const url = '/signInRequestOtp';
    const res = await this.httpClient.post(url, signInData, false).toPromise() as Response;
    if (this.hasIssuedToken(res)) {
      this.httpClient.saveAuth(res.data.auth);
    }
    return res;
  }

  // OTP 검증 + 로그인 완료. 성공(또는 change_password) 시 토큰 저장.
  async verifyOtpAndSignIn(verifyData: any) {
    const url = '/signInVerifyOtp';
    const res = await this.httpClient.post(url, verifyData, false).toPromise() as Response;
    if (this.hasIssuedToken(res)) {
      this.httpClient.saveAuth(res.data.auth);
    }
    return res;
  }

  private hasIssuedToken(res: Response): boolean {
    const code = res?.resultCode;
    return (code === 'success' || code === 'change_password') && !!res?.data?.auth;
  }

  // OTP 인증 후 user 정보 저장. signInCompleted 단계 통합.
  saveSignInResult(userInfo: UserInfo) {
    this.userInfo = userInfo;
    this.saveUserInfo(userInfo);
    this.setUserInfoToMenu(userInfo);
  }

  async checkErpUser(id: string) {
    const url = '/checkLoginAccount';
    const data = {
      id
    };
    const res = await this.post(url, data).toPromise() as Response;
    return res;
  }

  /**
   * 비밀번호 변경. 현재 PW 는 SHA-256(base64), 신규 PW 는 AES-256-GCM payload(base64) 로 전송.
   * 서버에서 신규 PW 복호화 → 정책 검증 → SHA-256 변환 후 저장.
   */
  async changePassword(currentSha256: string, newEncryptedBase64: string): Promise<Response> {
    const url = '/updatePassword';
    const body = {
      id: this.userInfo?.id ?? '',
      password: currentSha256,
      changePassword: newEncryptedBase64
    };
    return await this.httpClient.post(url, body).toPromise() as Response;
  }

  getSignInId() {
    return this.userInfo?.id ?? '';
  }

  getUserInfo() {
    return this.userInfo;
  }

  isSignedIn() {
    let signedIn = false;
    if(this.userInfo) {
      const userId = this.userInfo.id;
      const authToken = this.auth.getAuthToken();
      if(!isStringEmpty(userId) && !isStringEmpty(authToken)) {
        signedIn = true;
      }
    }
    return signedIn;
  }



  isMasterAdmin() {
    return this.userInfo?.accountTypeCode === 'USER100130133';
  }

  isSlaveAdmin() {
    return this.userInfo?.accountTypeCode === 'USER100130132';
  }

  saveUserInfo(userInfo: UserInfo) {
    sessionStorage.setItem('user', JSON.stringify(userInfo));
  }

  removeUserId() {
    sessionStorage.removeItem('user');
  }

  getUserInfoFromStorage() {
    let userInfo = undefined;
    const userInfoString = sessionStorage.getItem('user');
    if(userInfoString !== null) {
      userInfo = JSON.parse(userInfoString);
    }
    return userInfo;
  }

  setUserInfoToMenu(userInfo?: UserInfo) {
    if(userInfo) {
      this.navigation.setUserInfo(userInfo);
    }
  }

  isMustChangePassword(): boolean {
    return sessionStorage.getItem('must_change_pw') === 'true';
  }

  setMustChangePassword(val: boolean): void {
    if (val) {
      sessionStorage.setItem('must_change_pw', 'true');
    } else {
      sessionStorage.removeItem('must_change_pw');
    }
  }

  signOut() {
    this.removeSignInInfo();
    this.router.navigateByUrl('/signin', {replaceUrl: true});
  }

  removeSignInInfo() {
    this.removeUserId();
    this.httpClient.removeAuth();
    sessionStorage.removeItem('must_change_pw');
  }

  post(url: string, data: any, showLoading: boolean = true) {
    return this.httpClient.post(url, data, showLoading).pipe(tap((res: Response) => {
      if(!isSuccess(res.resultCode)) {
        this.utils.openMessageDialog('로그인 실패', res.resultMsg);
      }
    }));
  }
}

export interface SignInfo {
  id: string;
  name: string;
}
